import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const IntegrationStatusPanel = ({ integrations, onRefresh }) => {
  const [statusData, setStatusData] = useState(integrations);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const integrationServices = [
    {
      id: 'crm_salesforce',
      name: 'Salesforce CRM',
      type: 'CRM',
      status: 'connected',
      lastSync: new Date(Date.now() - 15 * 60 * 1000),
      icon: 'Database',
      description: 'Customer relationship management sync'
    },
    {
      id: 'payment_stripe',
      name: 'Stripe Payments',
      type: 'Payment',
      status: 'connected',
      lastSync: new Date(Date.now() - 5 * 60 * 1000),
      icon: 'CreditCard',
      description: 'Payment processing integration'
    },
    {
      id: 'telephony_twilio',
      name: 'Twilio Voice',
      type: 'Telephony',
      status: 'error',
      lastSync: new Date(Date.now() - 2 * 60 * 60 * 1000),
      icon: 'Phone',
      description: 'Call routing and recording'
    },
    {
      id: 'analytics_ga',
      name: 'Google Analytics',
      type: 'Analytics',
      status: 'connected',
      lastSync: new Date(Date.now() - 30 * 60 * 1000),
      icon: 'BarChart3',
      description: 'Lead tracking and analytics'
    },
    {
      id: 'email_sendgrid',
      name: 'SendGrid Email',
      type: 'Communication',
      status: 'warning',
      lastSync: new Date(Date.now() - 45 * 60 * 1000),
      icon: 'Mail',
      description: 'Email notifications and campaigns'
    },
    {
      id: 'calendar_outlook',
      name: 'Outlook Calendar',
      type: 'Calendar',
      status: 'disconnected',
      lastSync: null,
      icon: 'Calendar',
      description: 'Appointment scheduling integration'
    }
  ];

  useEffect(() => {
    setStatusData(integrationServices);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-error';
      case 'disconnected':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-success/10 text-success';
      case 'warning':
        return 'bg-warning/10 text-warning';
      case 'error':
        return 'bg-error/10 text-error';
      case 'disconnected':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'XCircle';
      case 'disconnected':
        return 'Circle';
      default:
        return 'HelpCircle';
    }
  };

  const formatLastSync = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return date?.toLocaleDateString();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Update last sync times for connected services
    const updatedData = statusData?.map(service => ({
      ...service,
      lastSync: service?.status === 'connected' ? new Date() : service?.lastSync
    }));
    
    setStatusData(updatedData);
    setIsRefreshing(false);
    
    if (onRefresh) {
      onRefresh(updatedData);
    }
  };

  const connectedCount = statusData?.filter(s => s?.status === 'connected')?.length;
  const totalCount = statusData?.length;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Zap" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Integration Status</h3>
            <p className="text-sm text-muted-foreground">
              {connectedCount} of {totalCount} integrations active
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          loading={isRefreshing}
          iconName="RefreshCw"
        >
          Refresh Status
        </Button>
      </div>
      {/* Integration Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {statusData?.map((integration) => (
          <div
            key={integration?.id}
            className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth"
          >
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg bg-card ${getStatusColor(integration?.status)}`}>
                <Icon name={integration?.icon} size={16} />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h4 className="text-sm font-medium text-foreground">
                    {integration?.name}
                  </h4>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(integration?.status)}`}>
                    {integration?.status}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {integration?.description}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Last sync: {formatLastSync(integration?.lastSync)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Icon 
                name={getStatusIcon(integration?.status)} 
                size={16} 
                className={getStatusColor(integration?.status)} 
              />
              <Button
                variant="ghost"
                size="sm"
                iconName="Settings"
                title="Configure integration"
              />
            </div>
          </div>
        ))}
      </div>
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" iconName="Plus">
          Add Integration
        </Button>
        <Button variant="outline" size="sm" iconName="Settings">
          Manage All
        </Button>
        <Button variant="outline" size="sm" iconName="Download">
          Export Config
        </Button>
        <Button variant="outline" size="sm" iconName="HelpCircle">
          Integration Guide
        </Button>
      </div>
      {/* Health Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-success">{connectedCount}</p>
            <p className="text-xs text-muted-foreground">Connected</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-warning">
              {statusData?.filter(s => s?.status === 'warning')?.length}
            </p>
            <p className="text-xs text-muted-foreground">Warnings</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-error">
              {statusData?.filter(s => s?.status === 'error')?.length}
            </p>
            <p className="text-xs text-muted-foreground">Errors</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-muted-foreground">
              {statusData?.filter(s => s?.status === 'disconnected')?.length}
            </p>
            <p className="text-xs text-muted-foreground">Inactive</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationStatusPanel;